import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import styles from './index.module.scss';

const GalaxyBackground = () => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    let width = window.innerWidth;
    let height = window.innerHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 0, 50);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000);
    if (mountRef.current) {
      mountRef.current.appendChild(renderer.domElement);
    }

    // Galaxy
    const galaxyGeometry = new THREE.BufferGeometry();
    const galaxyMaterial = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true
    });

    const galaxyPositions = [];
    const galaxyColors = [];
    const galaxySize = 100000;

    for (let i = 0; i < galaxySize; i++) {
      const theta = THREE.MathUtils.randFloatSpread(360);
      const phi = THREE.MathUtils.randFloatSpread(360);

      const x = Math.sin(theta) * Math.cos(phi) * Math.random() * 50;
      const y = Math.sin(theta) * Math.sin(phi) * Math.random() * 50;
      const z = Math.cos(theta) * Math.random() * 50;

      galaxyPositions.push(x, y, z);

      const r = Math.random();
      const g = Math.random() * 0.5;
      const b = Math.random();

      galaxyColors.push(r, g, b);
    }

    galaxyGeometry.setAttribute('position', new THREE.Float32BufferAttribute(galaxyPositions, 3));
    galaxyGeometry.setAttribute('color', new THREE.Float32BufferAttribute(galaxyColors, 3));

    const galaxy = new THREE.Points(galaxyGeometry, galaxyMaterial);
    scene.add(galaxy);

    // Bright stars
    const brightStarsGeometry = new THREE.BufferGeometry();
    const brightStarsMaterial = new THREE.PointsMaterial({
      size: 0.2,
      color: 0xffffff,
      blending: THREE.AdditiveBlending,
      transparent: true
    });

    const brightStarsPositions = [];
    const brightStarsSize = 1000;

    for (let i = 0; i < brightStarsSize; i++) {
      const x = THREE.MathUtils.randFloatSpread(100);
      const y = THREE.MathUtils.randFloatSpread(100);
      const z = THREE.MathUtils.randFloatSpread(100);

      brightStarsPositions.push(x, y, z);
    }

    brightStarsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(brightStarsPositions, 3));

    const brightStars = new THREE.Points(brightStarsGeometry, brightStarsMaterial);
    scene.add(brightStars);

    // Post-processing
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));

    const bloomPass = new UnrealBloomPass(new THREE.Vector2(width, height), 1.5, 0.2, 0.85);
    composer.addPass(bloomPass);

    // Animation
    const animate = () => {
      requestAnimationFrame(animate);

      galaxy.rotation.y += 0.0002;
      brightStars.rotation.y += 0.0001;

      composer.render();
    };

    animate();

    // Resize handler
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();

      renderer.setSize(width, height);
      composer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className={styles.galaxyBackground}></div>;
};

export default GalaxyBackground;